import { useRef, useCallback } from 'react';
import fire from '@store/firebase';

/**
 * Firebase Storage 다운로드 URL에서 스토리지 경로를 추출한다.
 * 예: https://firebasestorage.googleapis.com/v0/b/xxx.appspot.com/o/setting%2FsoundEffect%2Ffile.mp3?alt=media&token=xxx
 * → "setting/soundEffect/file.mp3"
 */
function extractStoragePath(downloadURL: string): string | null {
  try {
    const match = downloadURL.match(/\/o\/([^?]+)/);
    if (match) return decodeURIComponent(match[1]);
  } catch {}
  return null;
}

/**
 * 만료된 Firebase Storage URL을 SDK로 갱신하여 fresh download URL을 반환한다.
 */
async function refreshDownloadURL(
  oldURL: string,
): Promise<string | null> {
  const path = extractStoragePath(oldURL);
  if (!path) return null;
  try {
    return await fire.storage().ref(path).getDownloadURL();
  } catch {
    return null;
  }
}

/**
 * 사운드 재생 훅.
 * play() 호출 시 Audio를 즉시 생성하여 재생한다.
 * 403 등 재생 실패 시 Storage SDK로 URL을 갱신하고 재시도한다.
 */
export function useAudioPlayer(
  soundMap: { [key: string]: string | undefined },
  volume: number = 0.3,
) {
  const soundMapRef = useRef(soundMap);
  soundMapRef.current = soundMap;

  const volumeRef = useRef(volume);
  volumeRef.current = volume;

  // 갱신된 URL 캐시 (key → freshURL)
  const refreshedRef = useRef<Record<string, string>>({});

  const play = useCallback((key: string) => {
    const originalUrl = soundMapRef.current[key];
    // 갱신 캐시가 있으면 우선 사용
    const url = refreshedRef.current[key] || originalUrl;

    if (!url) {
      // fallback: key에 해당하는 사운드가 없으면 첫 번째 유효한 URL 재생
      const fallbackKey = Object.keys(soundMapRef.current).find(
        (k) => refreshedRef.current[k] || soundMapRef.current[k],
      );
      if (fallbackKey) {
        const fallbackUrl =
          refreshedRef.current[fallbackKey] || soundMapRef.current[fallbackKey];
        if (fallbackUrl) {
          const audio = new Audio(fallbackUrl);
          audio.volume = volumeRef.current;
          audio.play().catch(() => {});
        }
      }
      return;
    }

    const audio = new Audio(url);
    audio.volume = volumeRef.current;
    audio.play().catch(async () => {
      // 재생 실패 시 (403 등) → Storage SDK로 URL 갱신 후 재시도
      if (!originalUrl) return;
      const freshURL = await refreshDownloadURL(originalUrl);
      if (freshURL && freshURL !== url) {
        refreshedRef.current[key] = freshURL;
        const retryAudio = new Audio(freshURL);
        retryAudio.volume = volumeRef.current;
        retryAudio.play().catch(() => {});
      }
    });
  }, []);

  return { play };
}
